import {
  activeFormBindings,
  fetch_requestor_entity_types,
  fetch_requestor_form_details,
  getXVerdictFullConfigByEntityType,
  getXVerdictFullConfigById,
  get_entity_type_name,
  listXVerdictEntityConfigs,
  mapEntityConfigToCaseTypeOption,
  normalize_text,
  to_number,
} from './request_portal_helpers';
import { bound_form_ids, combined_form_details } from './request_portal_bound_forms';

function project_params(context) {
  return context.project_id ? { project_id: context.project_id } : {};
}

function case_type_load_error(error) {
  return error?.response?.data?.message
    || error?.message
    || 'Failed to load case types for this project.';
}

function entity_type_option(entity_type = {}) {
  return {
    id: to_number(entity_type?.id),
    title: get_entity_type_name(entity_type) || `Case type ${entity_type?.id}`,
    meta: normalize_text(entity_type?.entity_type_description || entity_type?.description || 'Configured case type'),
    project_id: to_number(entity_type?.project_id || entity_type?.project?.id),
    config_data: null,
    form_ids: [],
  };
}

function has_form_bindings(case_type = {}) {
  return case_type?.config_data && Array.isArray(case_type.config_data?.form_bindings);
}

async function fallback_case_type_options() {
  const entity_types = await fetch_requestor_entity_types();
  return entity_types.map(entity_type_option).filter((item) => item.id);
}

async function configured_case_type_options(context) {
  const config_rows = await listXVerdictEntityConfigs(project_params(context));
  return config_rows.map(mapEntityConfigToCaseTypeOption).filter(Boolean);
}

async function entity_type_config(context, case_type = {}) {
  return getXVerdictFullConfigByEntityType({
    entity_type_id: case_type.id,
    ...project_params(context),
  });
}

async function case_type_config(context, case_type = {}) {
  if (has_form_bindings(case_type)) {
    return case_type.config_data;
  }

  if (!case_type?.id) {
    return getXVerdictFullConfigById({ entity_config_id: case_type?.entity_config_id });
  }

  try {
    return await entity_type_config(context, case_type);
  } catch (error) {
    if (!case_type?.entity_config_id) throw error;
    return getXVerdictFullConfigById({ entity_config_id: case_type.entity_config_id });
  }
}

export default {
  async initialize_portal() {
    await this.load_case_types();
    await this.load_open_cases();
  },
  show_error_message(message) {
    this.portal_error = message || 'Something went wrong.';
  },
  clear_modal_state() {
    this.modal_error = '';
    this.validation_errors = [];
    this.selected_entity_type_id = null;
    this.selected_form_id = null;
    this.selected_form_details = {};
    this.answer_values = [];
  },
  async load_case_types() {
    this.loading_case_types = true;
    this.portal_error = '';

    try {
      const configured_options = await configured_case_type_options(this);
      this.case_type_options = configured_options.length
        ? configured_options
        : await fallback_case_type_options();
    } catch (error) {
      this.case_type_options = [];
      this.show_error_message(case_type_load_error(error));
    } finally {
      this.loading_case_types = false;
    }
  },
  async ensure_case_type_config(case_type) {
    return case_type_config(this, case_type);
  },
  async select_case_type(entity_type_id) {
    this.selected_entity_type_id = to_number(entity_type_id);
    this.selected_form_id = null;
    this.selected_form_details = {};
    this.answer_values = [];
    this.validation_errors = [];
    this.modal_error = '';

    if (!this.selected_entity_type_id) return;

    this.loading_case_form = true;
    try {
      const config_data = await this.ensure_case_type_config(this.selected_case_type);
      const form_ids = bound_form_ids(config_data, this.selected_case_type);

      if (!form_ids.length || !activeFormBindings(config_data).length) {
        this.modal_error = 'No active form is currently assigned to this case type.';
        return;
      }

      const form_details_list = await Promise.all(
        form_ids.map((form_id) => fetch_requestor_form_details(form_id))
      );
      const active_form_details = form_details_list.filter((form_details) => form_details?.is_active !== false);

      if (!active_form_details.length) {
        this.modal_error = 'The assigned form is not active yet.';
        this.selected_form_details = {};
        this.selected_form_id = null;
        return;
      }

      this.selected_form_id = form_ids[0];
      this.selected_form_details = combined_form_details(active_form_details);
    } catch (error) {
      this.modal_error = error?.response?.data?.message
        || error?.message
        || 'Failed to load the form assigned to this case type.';
    } finally {
      this.loading_case_form = false;
    }
  },
};
